import React, { useState, useRef } from "react";
import { Upload, X, Check, AlertCircle, ArrowRight, FileSpreadsheet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const API = process.env.REACT_APP_BACKEND_URL;

export const CSVImportDialog = ({ open, onClose, onImportComplete }) => {
  const [step, setStep] = useState("upload"); // upload, mapping, importing
  const [csvData, setCsvData] = useState(null);
  const [columns, setColumns] = useState([]);
  const [mapping, setMapping] = useState({});
  const [appFields, setAppFields] = useState([]);
  const [previewRows, setPreviewRows] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);

  const resetState = () => {
    setStep("upload");
    setCsvData(null);
    setColumns([]);
    setMapping({});
    setAppFields([]);
    setPreviewRows([]);
    setTotalRows(0);
    setLoading(false);
    setError(null);
    setImportResult(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      setError("Please select a CSV file");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API}/api/admin/applicants/preview-csv`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: formData
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || "Failed to parse CSV");
      }

      const data = await response.json();
      setCsvData(data.csv_data);
      setColumns(data.columns);
      setMapping(data.suggested_mapping);
      setAppFields(data.app_fields);
      setPreviewRows(data.preview_rows);
      setTotalRows(data.total_rows);
      setStep("mapping");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleMappingChange = (csvColumn, appField) => {
    setMapping(prev => ({ ...prev, [csvColumn]: appField }));
  };

  const validateMapping = () => {
    const mappedFields = Object.values(mapping);
    const hasFullName = mappedFields.includes('full_name');
    const hasEmail = mappedFields.includes('email');
    return hasFullName && hasEmail;
  };

  const handleImport = async () => {
    if (!validateMapping()) {
      setError("Please map both Full Name and Email fields");
      return;
    }

    setLoading(true);
    setError(null);
    setStep("importing");

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API}/api/admin/applicants/import-csv-mapped`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          csv_data: csvData,
          column_mapping: mapping
        })
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || "Import failed");
      }

      const result = await response.json();
      setImportResult(result);
      
      if (result.imported > 0 && onImportComplete) {
        onImportComplete(result);
      }
    } catch (err) {
      setError(err.message);
      setStep("mapping");
    } finally {
      setLoading(false);
    }
  };

  const getMappedFieldLabel = (fieldValue) => {
    const field = appFields.find(f => f.value === fieldValue);
    return field ? field.label : fieldValue;
  };

  const isFieldMapped = (fieldValue) => {
    return Object.values(mapping).includes(fieldValue);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-zinc-900 border-zinc-700 text-white">
        <DialogHeader>
          <DialogTitle className="text-crew-yellow flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5" />
            Import Applicants from CSV
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            {step === "upload" && "Select a CSV file containing applicant data"}
            {step === "mapping" && "Map your CSV columns to the registration form fields"}
            {step === "importing" && (importResult ? "Import complete!" : "Importing applicants...")}
          </DialogDescription>
        </DialogHeader>

        {error && (
          <div className="flex items-center gap-2 p-3 bg-red-500/20 border border-red-500/50 rounded text-red-400 text-sm">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {error}
          </div>
        )}

        {/* Step 1: Upload */}
        {step === "upload" && (
          <div className="py-8">
            <input
              type="file"
              ref={fileInputRef}
              accept=".csv"
              onChange={handleFileSelect}
              className="hidden"
            />
            <div 
              onClick={() => !loading && fileInputRef.current?.click()}
              className={`border-2 border-dashed border-zinc-700 rounded-lg p-12 text-center cursor-pointer hover:border-crew-yellow hover:bg-zinc-800/50 transition-colors ${loading ? 'opacity-50 cursor-wait' : ''}`}
            >
              <Upload className="w-12 h-12 mx-auto mb-4 text-zinc-500" />
              <p className="text-lg font-medium mb-2">
                {loading ? "Analyzing CSV..." : "Click to select CSV file"}
              </p>
              <p className="text-sm text-zinc-500">
                or drag and drop your file here
              </p>
            </div>

            <div className="mt-6 p-4 bg-zinc-800/50 rounded-lg">
              <h4 className="font-medium text-sm mb-2 text-zinc-300">Expected CSV Format</h4>
              <p className="text-xs text-zinc-500 mb-2">
                Your CSV should have a header row. Common columns include:
              </p>
              <div className="flex flex-wrap gap-2">
                {['Full Name', 'Email', 'Phone', 'Town', 'Preferred Name', 'Experience'].map(col => (
                  <span key={col} className="px-2 py-1 bg-zinc-700 rounded text-xs text-zinc-300">{col}</span>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Mapping */}
        {step === "mapping" && (
          <div className="space-y-4">
            {/* Required fields indicator */}
            <div className="flex items-center gap-4 p-3 bg-zinc-800/50 rounded">
              <span className="text-sm text-zinc-400">Required fields:</span>
              <span className={`text-sm ${isFieldMapped('full_name') ? 'text-green-400' : 'text-red-400'}`}>
                {isFieldMapped('full_name') ? '✓' : '✗'} Full Name
              </span>
              <span className={`text-sm ${isFieldMapped('email') ? 'text-green-400' : 'text-red-400'}`}>
                {isFieldMapped('email') ? '✓' : '✗'} Email
              </span>
            </div>

            {/* Column mapping table */}
            <div className="border border-zinc-700 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-zinc-800">
                  <tr>
                    <th className="text-left py-3 px-4 text-zinc-400 font-medium">CSV Column</th>
                    <th className="text-center py-3 px-4 text-zinc-400 font-medium w-12"></th>
                    <th className="text-left py-3 px-4 text-zinc-400 font-medium">Maps To</th>
                    <th className="text-left py-3 px-4 text-zinc-400 font-medium">Sample Data</th>
                  </tr>
                </thead>
                <tbody>
                  {columns.map((col, idx) => (
                    <tr key={col} className="border-t border-zinc-700/50">
                      <td className="py-3 px-4">
                        <span className="font-mono text-crew-yellow">{col}</span>
                      </td>
                      <td className="py-3 px-4 text-center text-zinc-500">
                        <ArrowRight className="w-4 h-4 inline" />
                      </td>
                      <td className="py-3 px-4">
                        <Select value={mapping[col] || "_skip"} onValueChange={(val) => handleMappingChange(col, val)}>
                          <SelectTrigger className="w-[220px] bg-black border-zinc-700 text-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-zinc-900 border-zinc-700">
                            {appFields.map(field => (
                              <SelectItem 
                                key={field.value} 
                                value={field.value}
                                className={`text-white hover:bg-zinc-800 ${field.required ? 'font-medium' : ''}`}
                              >
                                {field.label}
                                {field.required && <span className="text-red-400 ml-1">*</span>}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="py-3 px-4 text-zinc-500 text-xs max-w-[200px] truncate">
                        {previewRows[0]?.[col] || "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Preview */}
            <div>
              <h4 className="font-medium text-sm mb-2 text-zinc-300">
                Preview ({totalRows} rows total, showing first {Math.min(5, previewRows.length)})
              </h4>
              <div className="overflow-x-auto border border-zinc-700 rounded-lg">
                <table className="w-full text-xs">
                  <thead className="bg-zinc-800">
                    <tr>
                      {columns.map(col => (
                        <th key={col} className="text-left py-2 px-3 text-zinc-400 whitespace-nowrap">
                          {mapping[col] && mapping[col] !== "_skip" ? getMappedFieldLabel(mapping[col]) : <span className="text-zinc-600">{col}</span>}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewRows.map((row, idx) => (
                      <tr key={idx} className="border-t border-zinc-700/50">
                        {columns.map(col => (
                          <td key={col} className={`py-2 px-3 whitespace-nowrap ${mapping[col] === "_skip" ? 'text-zinc-600' : 'text-zinc-300'}`}>
                            {row[col] || "-"}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={resetState} className="border-zinc-700 text-zinc-300">
                <X className="w-4 h-4 mr-2" /> Start Over
              </Button>
              <Button 
                onClick={handleImport} 
                disabled={!validateMapping() || loading}
                className="bg-crew-yellow text-black font-bold hover:bg-white"
              >
                {loading ? "Importing..." : `Import ${totalRows} Applicants`}
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Result */}
        {step === "importing" && importResult && (
          <div className="py-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center">
              <Check className="w-8 h-8 text-green-400" />
            </div>
            <h3 className="text-xl font-bold text-green-400 mb-2">Import Complete!</h3>
            <p className="text-zinc-400 mb-4">{importResult.message}</p>
            
            <div className="flex justify-center gap-6 mb-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-crew-yellow">{importResult.imported}</div>
                <div className="text-xs text-zinc-500">Imported</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-zinc-400">{importResult.skipped}</div>
                <div className="text-xs text-zinc-500">Skipped</div>
              </div>
            </div>

            {importResult.errors && importResult.errors.length > 0 && (
              <div className="text-left p-3 bg-zinc-800 rounded text-xs text-zinc-500 mb-4 max-h-32 overflow-y-auto">
                <p className="font-medium mb-1">Notes:</p>
                {importResult.errors.map((err, i) => (
                  <p key={i}>{err}</p>
                ))}
              </div>
            )}

            <Button onClick={handleClose} className="bg-crew-yellow text-black font-bold hover:bg-white">
              Done
            </Button>
          </div>
        )}

        {step === "importing" && !importResult && (
          <div className="py-12 text-center">
            <div className="w-12 h-12 mx-auto mb-4 border-4 border-crew-yellow border-t-transparent rounded-full animate-spin"></div>
            <p className="text-zinc-400">Importing applicants...</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
